import { useCallback, useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";

import { AOO_API_HOST } from "../../../env";
import { verifyMachineIsScannable } from "./api/machineEditorApi";

export type MachineConfirmationStage =
  | "idle"
  | "waiting"
  | "confirmed"
  | "timedOut";

/**
 * Polly-style wait-and-retry: each attempt backs off further than the last, so a machine that lands
 * in the database a beat late is still caught without hammering the API. Roughly 15s in total.
 */
const BACKOFF_MS = [250, 500, 1000, 2000, 3500, 4000, 4000];

const HUB_URL = `${AOO_API_HOST}/api/log`;

export interface MachineCreationConfirmation {
  stage: MachineConfirmationStage;
  /** True once the hub told us the machine was created, whether or not the read has caught up yet. */
  signalReceived: boolean;
  attempts: number;
  /**
   * Resolves true once the machine is confirmed to exist and resolve by id - which is exactly what
   * scanning its QR code does. Only then is it safe to show the code for printing.
   */
  confirm: (machineId: string) => Promise<boolean>;
  reset: () => void;
}

/**
 * Gates the QR code preview behind proof that the new machine is really there.
 *
 * Two things race: the `MachineCreated` signal the AOO hub broadcasts once Manufacturing.API has
 * committed the machine, and a verification read that repeats on a widening cooldown. The signal
 * cuts the current cooldown short; the read is what actually decides, because it is the same lookup
 * a scanner performs.
 */
export const useMachineCreationConfirmation = (
  isActive: boolean
): MachineCreationConfirmation => {
  const [stage, setStage] = useState<MachineConfirmationStage>("idle");
  const [signalReceived, setSignalReceived] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const connectionRef = useRef<HubConnection | null>(null);
  const awaitedMachineIdRef = useRef<string | null>(null);
  const signalResolverRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The connection is opened while the popover is open, before anything is saved, so the signal for
  // a machine created moments later cannot be missed.
  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        transport: HttpTransportType.WebSockets,
        withCredentials: true,
      })
      .configureLogging(LogLevel.Warning)
      .withAutomaticReconnect()
      .build();

    connection.on("MachineCreated", (jsonData: string) => {
      let createdId: string | undefined;

      try {
        createdId = JSON.parse(jsonData)?.MachineId;
      } catch (e) {
        console.error("Unreadable MachineCreated payload", e);
        return;
      }

      const awaited = awaitedMachineIdRef.current;
      if (!awaited || !createdId || createdId.toLowerCase() !== awaited.toLowerCase()) {
        return;
      }

      if (isMountedRef.current) {
        setSignalReceived(true);
      }

      // Cut short whatever cooldown the verification loop is sitting in.
      signalResolverRef.current?.();
    });

    connectionRef.current = connection;

    connection.start().catch((e) => {
      // A dead hub must not block the workflow - the verification read still decides.
      console.error("Could not connect to the log hub", e);
    });

    return () => {
      connectionRef.current = null;
      connection.off("MachineCreated");

      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => undefined);
      }
    };
  }, [isActive]);

  const reset = useCallback(() => {
    awaitedMachineIdRef.current = null;
    signalResolverRef.current = null;
    setStage("idle");
    setSignalReceived(false);
    setAttempts(0);
  }, []);

  const confirm = useCallback(async (machineId: string) => {
    awaitedMachineIdRef.current = machineId;
    setStage("waiting");
    setSignalReceived(false);
    setAttempts(0);

    for (let attempt = 0; attempt < BACKOFF_MS.length; attempt += 1) {
      if (isMountedRef.current) {
        setAttempts(attempt + 1);
      }

      if (await verifyMachineIsScannable(machineId)) {
        if (isMountedRef.current) {
          setStage("confirmed");
        }
        return true;
      }

      // Wait out the cooldown, unless the hub tells us the machine landed first.
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          signalResolverRef.current = null;
          resolve();
        }, BACKOFF_MS[attempt]);

        signalResolverRef.current = () => {
          clearTimeout(timer);
          signalResolverRef.current = null;
          resolve();
        };
      });
    }

    // One last read after the final cooldown.
    if (await verifyMachineIsScannable(machineId)) {
      if (isMountedRef.current) {
        setStage("confirmed");
      }
      return true;
    }

    if (isMountedRef.current) {
      setStage("timedOut");
    }
    return false;
  }, []);

  return { stage, signalReceived, attempts, confirm, reset };
};
